<template>
  <v-row>
    <v-row>
      <v-col class="align-left">
        <ExpandCollapseToggle
          id="deckPanelToggle"
          @toggleAllPanels="toggleAllPanels"
          styleClass="mt-3"
          :panel="panel"
        />
      </v-col>
      <v-col class="align-right mt-2">
        <ValidationCountsHeader v-if="moduleType == MODULE_TYPES.MANAGEMENT" />
      </v-col>
    </v-row>
    <v-form v-model="valid" style="display: contents" ref="inspectionDeckForm">
      <v-expansion-panels v-model="panel" multiple color="#fff">
        <v-expansion-panel style="margin-top: 20px">
          <v-expansion-panel-title
            class="d-flex justify-start"
            hide-actions
            expand-icon="fa-solid fa-plus"
            collapse-icon="fa-solid fa-minus"
          >
            <v-icon v-if="!panel.includes(0)" icon="fa-solid fa-plus" />
            <v-icon v-if="panel.includes(0)" icon="fa-solid fa-minus" />
            <h3>Deck Conditions</h3>
          </v-expansion-panel-title>
          <v-expansion-panel-text eager>
            <v-row>
              <v-col xl="6" lg="6" md="6" sm="12" cols="12">
                <LabelSelect
                  labelXl="6"
                  labelLg="7"
                  labelMd="7"
                  labelSm="7"
                  selectXl="5"
                  selectLg="4"
                  selectMd="5"
                  selectSm="5"
                  id="deckCondRating"
                  showDurationIcon
                  :tooltip="getHistDuration(deckValue.dkRatingDuration, deckValue.dkRatingDurationChanges)"
                  label="B.C.01 Deck Condition Rating"
                  :refTable="REFERENCE_TABLE.CONDITION_RATING"
                  :isEditable="isEditable"
                  :isRequired="isEditable"
                  @update:modelValue="
                    {
                      deckConditionUpdated = true;
                      conditionUpdated = true;
                    }
                  "
                  v-model="deckValue.dkRating"
                ></LabelSelect
              ></v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <CommentTypeTextField
                  v-if="moduleType == MODULE_TYPES.INSPECTION"
                  label="Overall Deck Notes"
                  id="field_Overall_Deck_Notes"
                  :inspectionCommentType="INSPECTION_COMMENTS.OVERALL_DECK_COMMENTS"
                  :readonly="!isEditable"
                  :counter="commentsMaxLength"
                  :maxlength="commentsMaxLength"
                  @changesDetected="inspectionStore.setDirtyFlag(true)"
                />
                <ManagementCommentTypeTextField
                  v-else
                  label="Overall Deck Notes"
                  id="field_Overall_Deck_Notes"
                  :inspectionCommentType="INSPECTION_COMMENTS.OVERALL_DECK_COMMENTS"
                  :readonly="!isEditable"
                  :counter="commentsMaxLength"
                  :maxlength="commentsMaxLength"
                  :comments="deckValue.inspectionComments"
                  :brkey="deckValue.brkey"
                  :inspectionId="deckValue.inspectionId"
                  @updateComment="(comment) => setUpdatedComment(comment, INSPECTION_COMMENTS.OVERALL_DECK_COMMENTS)"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col xl="6" lg="6" md="6" sm="12" cols="12">
                <LabelSelect
                  labelXl="6"
                  labelLg="7"
                  labelMd="7"
                  labelSm="7"
                  selectXl="5"
                  selectLg="4"
                  selectMd="5"
                  selectSm="5"
                  id="surfaceCondRating"
                  showDurationIcon
                  :tooltip="getHistDuration(deckValue.deckWsCondRateDuration, deckValue.deckWsCondRateDurationChanges)"
                  label="DK.01 Wearing Surface Condition Rating"
                  :refTable="REFERENCE_TABLE.CONDITION_RATING"
                  :isEditable="isEditable"
                  :isRequired="isEditable"
                  @update:modelValue="
                    {
                      surfaceConditionUpdated = true;
                      conditionUpdated = true;
                    }
                  "
                  v-model="deckValue.deckWsCondRate"
                ></LabelSelect></v-col
            ></v-row>

            <v-row>
              <v-col cols="12">
                <CommentTypeTextField
                  v-if="moduleType == MODULE_TYPES.INSPECTION"
                  label="Overall Wearing Surface Notes"
                  id="field_Overall_Wearing_Surface_Notes"
                  :inspectionCommentType="INSPECTION_COMMENTS.OVERALL_WEARING_SURFACE_COMMENTS"
                  :readonly="!isEditable"
                  :counter="commentsMaxLength"
                  :maxlength="commentsMaxLength"
                  @changesDetected="inspectionStore.setDirtyFlag(true)"
                />
                <ManagementCommentTypeTextField
                  v-else
                  label="Overall Wearing Surface Notes"
                  id="field_Overall_Wearing_Surface_Notes"
                  :inspectionCommentType="INSPECTION_COMMENTS.OVERALL_WEARING_SURFACE_COMMENTS"
                  :readonly="!isEditable"
                  :counter="commentsMaxLength"
                  :maxlength="commentsMaxLength"
                  :comments="deckValue.inspectionComments"
                  :brkey="deckValue.brkey"
                  :inspectionId="deckValue.inspectionId"
                  @updateComment="
                    (comment) => setUpdatedComment(comment, INSPECTION_COMMENTS.OVERALL_WEARING_SURFACE_COMMENTS)
                  "
                />
              </v-col>
            </v-row>
          </v-expansion-panel-text>
        </v-expansion-panel>
        <v-expansion-panel style="margin-top: 8px">
          <v-expansion-panel-title
            class="d-flex justify-start"
            hide-actions
            expand-icon="fa-solid fa-plus"
            collapse-icon="fa-solid fa-minus"
          >
            <v-icon v-if="!panel.includes(1)" icon="fa-solid fa-plus" />
            <v-icon v-if="panel.includes(1)" icon="fa-solid fa-minus" />
            <h3>Deck Details</h3>
          </v-expansion-panel-title>
          <v-expansion-panel-text eager>
            <v-row>
              <v-col xl="6" lg="6" md="12" sm="12">
                <LabelText
                  labelXl="6"
                  labelLg="6"
                  labelMd="6"
                  labelSm="6"
                  textXl="6"
                  textLg="6"
                  textMd="6"
                  textSm="6"
                  :isEditable="isEditable"
                  id="nbis_Bridge_Length"
                  label="B.G.01 NBIS Bridge Length"
                  v-model="deckValue.nbisBridgeLength"
                  :roundingFactor="ROUNDING_FACTOR.TENTH"
                  type="number"
                  appendWith="ft"
                  :maxlength="8"
                  :decimalPlaces="1"
                  :rules="
                    isEditable
                      ? [
                          (value) => {
                            return valueInRangeRule(value, 0, 999999.9);
                          },
                        ]
                      : []
                  "
                >
                </LabelText>
                <LabelText
                  id="outToOutBridgeWidth"
                  label="B.G.05 Bridge Width (O/O)"
                  labelXl="6"
                  labelLg="6"
                  labelMd="6"
                  labelSm="6"
                  textXl="6"
                  textLg="6"
                  textMd="6"
                  textSm="6"
                  appendWith=" ft"
                  :counter="null"
                  type="number"
                  v-model="minimumOut2OutWidth"
                  maxlength="5"
                  :decimalPlaces="1"
                  :roundingFactor="ROUNDING_FACTOR.TENTH"
                />
                <LabelText
                  id="curbToCurbBridgeWidth"
                  label="B.G.06 Bridge Width (C/C)"
                  labelXl="6"
                  labelLg="6"
                  labelMd="6"
                  labelSm="6"
                  textXl="6"
                  textLg="6"
                  textMd="6"
                  textSm="6"
                  appendWith=" ft"
                  :counter="null"
                  type="number"
                  v-model="deckValue.roadWidth"
                  maxlength="5"
                  :decimalPlaces="1"
                  :roundingFactor="ROUNDING_FACTOR.TENTH"
                />
                <LabelText
                  id="leftCurbWidth"
                  label="B.G.07 Left Curb or Sidewalk Width"
                  labelXl="6"
                  labelLg="6"
                  labelMd="6"
                  labelSm="6"
                  textXl="6"
                  textLg="6"
                  textMd="6"
                  textSm="6"
                  appendWith=" ft"
                  :counter="null"
                  type="number"
                  v-model="deckValue.lftCurbSw"
                  maxlength="4"
                  :decimalPlaces="1"
                  :roundingFactor="ROUNDING_FACTOR.TENTH"
                />
                <LabelText
                  id="rightCurbWidth"
                  label="B.G.08 Right Curb or Sidewalk Width"
                  labelXl="6"
                  labelLg="6"
                  labelMd="6"
                  labelSm="6"
                  textXl="6"
                  textLg="6"
                  textMd="6"
                  textSm="6"
                  appendWith=" ft"
                  :counter="null"
                  type="number"
                  v-model="deckValue.rtCurbSw"
                  maxlength="4"
                  :decimalPlaces="1"
                  :roundingFactor="ROUNDING_FACTOR.TENTH"
                />
                <LabelSelect
                  id="bridgeMedian"
                  label="B.G.10 Bridge Median"
                  labelXl="6"
                  labelLg="6"
                  labelMd="6"
                  labelSm="6"
                  selectXl="6"
                  selectLg="6"
                  selectMd="6"
                  selectSm="6"
                  :refTable="REFERENCE_TABLE.BRIDGE_MEDIAN_TYPE"
                  v-model="deckValue.bridgeMed"
                />
                <LabelText
                  id="maxBridgeHeight"
                  label="B.G.13 Maximum Bridge Height"
                  :isEditable="isEditable"
                  :isRequired="isEditable && isRequiredByReportGroup"
                  labelXl="6"
                  labelLg="6"
                  labelMd="6"
                  labelSm="6"
                  textXl="6"
                  textLg="6"
                  textMd="6"
                  textSm="6"
                  appendWith=" ft"
                  :counter="null"
                  type="number"
                  v-model="deckValue.maxStructureHeight"
                  maxlength="4"
                  :roundingFactor="ROUNDING_FACTOR.ROUND"
                />
                <LabelText
                  id="irregularDeckArea"
                  label="B.G.15 Irregular Deck Area"
                  labelXl="6"
                  labelLg="6"
                  labelMd="6"
                  labelSm="6"
                  textXl="6"
                  textLg="6"
                  textMd="6"
                  textSm="6"
                  appendWith=" sf"
                  :counter="null"
                  type="number"
                  v-model="deckValue.irregularDeckArea"
                  maxlength="11"
                  :decimalPlaces="1"
                  :roundingFactor="ROUNDING_FACTOR.TENTH"
                />
                <LabelText
                  id="deckArea"
                  label="B.G.16 Calculated Deck Area"
                  labelXl="6"
                  labelLg="6"
                  labelMd="6"
                  labelSm="6"
                  textXl="6"
                  textLg="6"
                  textMd="6"
                  textSm="6"
                  appendWith=" sf"
                  :model-value="deckValue.deckArea"
                  :decimalPlaces="1"
                  type="number"
                  :roundingFactor="ROUNDING_FACTOR.TENTH"
                />
              </v-col>
              <v-col xl="6" lg="6" md="12" sm="12">
                <LabelText
                  labelXl="6"
                  labelLg="8"
                  labelMd="6"
                  labelSm="7"
                  textXl="5"
                  textLg="3"
                  textMd="4"
                  textSm="5"
                  id="estimatedCholrideContent"
                  :isEditable="isEditable"
                  label="DK.02 Estimated Chloride Content"
                  v-model="deckValue.estChlorideContentPct"
                  appendWith=" %"
                  textStyle="width:100%"
                  type="number"
                  :rules="
                    isEditable
                      ? [
                          (value) => {
                            return valueInRangeRule(value, 0, 100);
                          },
                        ]
                      : []
                  "
                  maxlength="3"
                >
                </LabelText>
                <LabelDatefield
                  labelXl="6"
                  labelLg="8"
                  labelMd="6"
                  labelSm="7"
                  textXl="5"
                  textLg="4"
                  textMd="6"
                  textSm="5"
                  textStyle="width:100%"
                  id="estimatedCholrideContentDate"
                  label="DK.03 Estimated Chloride Content Date"
                  v-model="deckValue.estChlorideContentDate"
                  :isEditable="isEditable"
                >
                </LabelDatefield>
                <LabelText
                  labelXl="6"
                  labelLg="8"
                  labelMd="6"
                  labelSm="7"
                  textXl="5"
                  textLg="3"
                  textMd="4"
                  textSm="5"
                  id="crackingMetric"
                  :isEditable="isEditable"
                  label="DK.04 Deck Cracking Metric"
                  :roundingFactor="ROUNDING_FACTOR.HUNDRETH"
                  v-model="deckValue.deckCrackingMetric"
                  appendWith=" YD/SY"
                  textStyle="width:100%"
                  style="min-width: 250px"
                  :decimalPlaces="2"
                  type="number"
                  :rules="isEditable ? [(greaterThanZeroRule, lessThanOneRule)] : []"
                  maxlength="4"
                >
                </LabelText>
                <LabelText
                  labelXl="6"
                  labelLg="8"
                  labelMd="6"
                  labelSm="7"
                  textXl="5"
                  textLg="3"
                  textMd="4"
                  textSm="5"
                  id="DeckProtectInstallYear"
                  label="DK.05 Protect Install Year"
                  v-model="deckValue.protectYear"
                  :isEditable="isEditable"
                  :rules="
                    isEditable && deckValue.protectYear
                      ? [
                          (value) => {
                            return minYearLengthRule(value, 4);
                          },
                        ]
                      : []
                  "
                  textStyle="width:60%"
                  type="number"
                  :showComma="false"
                  :maxlength="4"
                />
                <LabelText
                  labelXl="6"
                  labelLg="8"
                  labelMd="6"
                  labelSm="7"
                  textXl="5"
                  textLg="4"
                  textMd="6"
                  textSm="5"
                  id="DeckProtectInstallNote"
                  :isEditable="isEditable"
                  label="DK.06 Protect Install Note"
                  v-model="deckValue.protectNote"
                  textStyle="width:100%"
                  type="text"
                  maxlength="30"
                />

                <LabelText
                  labelXl="6"
                  labelLg="8"
                  labelMd="6"
                  labelSm="7"
                  textXl="5"
                  textLg="4"
                  textMd="6"
                  textSm="5"
                  id="WearingSurfaceThicknessMain"
                  :isEditable="isEditable"
                  label="DK.07 Wearing Surface Thickness"
                  v-model="deckValue.wearingSurfaceThickness"
                  @blur="updateWearingSurfacedThicknessDate()"
                  appendWith=" in"
                  type="number"
                  textStyle="width:100%"
                  maxlength="4"
                  :decimalPlaces="1"
                  :roundingFactor="ROUNDING_FACTOR.TENTH"
                  :rules="
                    isEditable
                      ? [
                          (value) => {
                            return lessThanValueRule(value, DECK_MAX_VALUE.WEARING_SURFACE_THICKNESS);
                          },
                        ]
                      : []
                  "
                >
                </LabelText>
                <LabelDatefield
                  labelXl="6"
                  labelLg="8"
                  labelMd="6"
                  labelSm="7"
                  textXl="5"
                  textLg="4"
                  textMd="6"
                  textSm="5"
                  textStyle="width:100%"
                  id="WearingSurfaceRecordedDateMain"
                  label="DK.08 Wearing Surface Thickness Date"
                  v-model="deckValue.wearingSurfaceThicknessDate"
                  :isEditable="isEditable"
                >
                </LabelDatefield>
                <LabelText
                  id="wearingSurfaceThicknessOver"
                  label="DK.09 Wearing Surface Thickness Over"
                  labelXl="6"
                  labelLg="8"
                  labelMd="6"
                  labelSm="7"
                  textXl="5"
                  textLg="4"
                  textMd="6"
                  textSm="5"
                  :isEditable="isEditable"
                  appendWith=" in"
                  :counter="null"
                  type="number"
                  v-model="deckValue.wearingSurfaceThicknessOver"
                  maxlength="4"
                  :decimalPlaces="1"
                  :roundingFactor="ROUNDING_FACTOR.TENTH"
                  :rules="
                    isEditable
                      ? [
                          (value) => {
                            return lessThanValueRule(value, 99);
                          },
                        ]
                      : []
                  "
                />
                <LabelText
                  id="wearingSurfaceThicknessUnder"
                  label="DK.10 Wearing Surface Thickness Under"
                  labelXl="6"
                  labelLg="8"
                  labelMd="6"
                  labelSm="7"
                  textXl="5"
                  textLg="4"
                  textMd="6"
                  textSm="5"
                  appendWith=" in"
                  :counter="null"
                  :isEditable="isEditable"
                  type="number"
                  v-model="deckValue.wearingSurfaceThicknessUnder"
                  maxlength="4"
                  :decimalPlaces="1"
                  :roundingFactor="ROUNDING_FACTOR.TENTH"
                  :rules="
                    isEditable
                      ? [
                          (value) => {
                            return lessThanValueRule(value, 99);
                          },
                        ]
                      : []
                  "
                />
              </v-col>
            </v-row>
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-form>
    <v-row class="pt-2">
      <v-col>
        <div
          v-if="
            deckValue.brkey &&
            !deckValue.structureUnits?.filter((structureUnit) => ['A', 'M'].includes(structureUnit.legacyStructureType))
              ?.length
          "
          class="text-center text-gray-500 py-4"
        >
          <v-label class="noRecordsMessage d-flex justify-center"
            >This structure currently contains no structure unit details.
          </v-label>
        </div>
        <v-row
          v-else
          v-for="structureUnit of deckValue.structureUnits?.filter((structureUnit) =>
            ['A', 'M'].includes(structureUnit.legacyStructureType),
          )"
          :key="structureUnit"
        >
          <v-col
            ><DeckSpanBySpan
              ref="deckSpanBySpanRef"
              :isEditable="isEditable"
              :isExpanded="isExpanded"
              :id="`spanBySpan_${structureUnit?.strUnitKey}`"
              :span="structureUnit"
              :structureHeader="structureHeader"
              @updateMinSpanOut2OutWidth="(value) => (structureUnit.minSpanOut2OutWidth = value)"
            />
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </v-row>
</template>
<script>
import { ref, computed, onMounted, watch } from "vue";
import { useInspectionStore } from "@/stores/inspection";
import { REFERENCE_TABLE } from "@/constants/ReferenceTables";
import { useConfigStore } from "@/stores/config";
import { getHistDuration } from "@/composables/util";
import { ENV_CONFIG_PROPERTY } from "@/constants/EnvConfigProperties";
import { INSPECTION_COMMENTS } from "@/constants/Inspections";
import CommentTypeTextField from "@/components/common/CommentTypeTextField.vue";
import { getFormattedDateStringNoTime } from "@/composables/dateField.js";
import {
  requiredRule,
  greaterThanZeroRule,
  lessThanOneRule,
  lessThanValueRule,
  minYearLengthRule,
  valueInRangeRule,
} from "@/composables/validationRules";
import LabelSelect from "@/components/common/LabelSelect.vue";
import LabelText from "@/components/common/LabelText.vue";
import { ROUNDING_FACTOR } from "@/constants/RoundingFactors";
import { DECK_MAX_VALUE, MODULE_TYPES } from "@/constants/InspectionConstants";
import LabelDatefield from "@/components/common/LabelDatefield.vue";
import useDeckData from "@/composables/deckData";
import ExpandCollapseToggle from "@/components/shared/ExpandCollapseToggle.vue";
import { useDeckStore } from "@/components/inspection/store/deck";
import { useQuickSearchStore } from "@/stores/quickSearch";
import { useInspectionLinksStore } from "@/stores/inspectionLinks";
import ManagementCommentTypeTextField from "@/components/common/ManagementCommentTypeTextField.vue";
import { INSPECTION_LINKS_DECK_PERMISSIONS } from "@/rbac";
import { ACCESS_LEVELS } from "@/rbac/accessLevels.js";
import { useUserPermissionStore } from "@/stores/userPermission";
import { storeToRefs } from "pinia";
import { INSPECTION_STATUSES } from "@/constants/InspectionStatuses";
import { SNACKBAR_MARKER, SNACKBAR_MESSAGE_TYPES } from "@/constants/GlobalSnackbar.js";
import DeckSpanBySpan from "@/components/inspection/spanBySpanComponents/DeckSpanBySpan.vue";
import ValidationCountsHeader from "@/components/shared/ValidationCountsHeader.vue";
import { useSnackbarStore } from "@/stores/snackbar";
import { COMMON_MESSAGE_CONSTANTS } from "@/constants/CommonWebConstants";
import { getMinimumOut2OutWidthDisplay } from "@/composables/spanBySpan";

export default {
  name: "InspectionDeck",
  props: {
    moduleType: { default: MODULE_TYPES.INSPECTION },
  },
  emits: ["showMessage"],
  setup(props) {
    const allPanels = [0, 1];
    const panel = ref(allPanels);
    const inspectionStore = useInspectionStore();
    const inspectionLinksStore = useInspectionLinksStore();
    let deckConditionUpdated = ref(false);
    const structure = computed(() => inspectionStore.selectedInspection);
    let surfaceConditionUpdated = ref(false);
    let conditionUpdated = ref(false);
    let valid = ref(false);
    let inspectionDeckForm = ref(null);
    const isExpanded = ref(true);
    const deckSpanBySpanRef = ref(null);
    const deckStore = useDeckStore();
    const quickSearchStore = useQuickSearchStore();
    const structureHeader = computed(() => quickSearchStore.getStructure());
    const { hasQuickSearchSucceeded } = storeToRefs(quickSearchStore);
    const snackbarStore = useSnackbarStore();
    let deckValue = ref({});
    let snackBarId = ref(null);
    let wearingSurfaceThicknessUpdated = ref(false);

    const configStore = useConfigStore();
    let resetDirty = ref(false);
    const userPermissionStore = useUserPermissionStore();

    const isEditable = computed(() => {
      if (props.moduleType == MODULE_TYPES.MANAGEMENT) {
        return hasQuickSearchSucceeded.value && deckValue.value?.brkey && hasAccessToEdit.value;
      } else {
        return inspectionStore.isEditable;
      }
    });

    if (!isEditable.value) {
      panel.value = allPanels;
    }

    onMounted(async () => {
      await readData();
    });

    const readData = async () => {
      deckValue.value = {};
      let isSuccess = false;
      resetDirty.value = true;
      wearingSurfaceThicknessUpdated.value = false;
      if (
        props.moduleType == MODULE_TYPES.MANAGEMENT &&
        structureHeader.value?.BRKEY &&
        structureHeader.value.INSPECTIONID
      ) {
        if (!hasQuickSearchSucceeded.value) {
          resetDirty.value = true;
          return;
        }
        isSuccess = await deckStore.loadDeckObject(
          props.moduleType,
          structureHeader.value.BRKEY,
          structureHeader.value.INSPECTIONID,
        );
        if (isSuccess) {
          deckValue.value = deckStore.getDeckObject();
        } else {
          deckStore.resetPageData();
          showErrorMessage();
        }
      } else if (props.moduleType == MODULE_TYPES.INSPECTION) {
        await deckStore.loadDeckObject(props.moduleType, null, null, structure.value);
        deckValue.value = deckStore.getDeckObject();
        const { getData } = useDeckData();
        deckValue.value.roadwayWidth = getData.value;
      }
      resetDirty.value = true;
      await validate();
    };

    const validate = async () => {
      await inspectionDeckForm.value.validate();
    };

    const conditionRatings = computed(() => configStore.getReferenceValues(REFERENCE_TABLE.CONDITION_RATING));

    const deckSurfaceTypes = computed(() => configStore.getReferenceValues(REFERENCE_TABLE.DECK_SURFACE_TYPE));

    const deckMembraneTypes = computed(() => configStore.getReferenceValues(REFERENCE_TABLE.DECK_MEMBRANE_TYPE));

    const deckProtectionTypes = computed(() => configStore.getReferenceValues(REFERENCE_TABLE.DECK_PROTECTION_TYPE));

    const commentsMaxLength = computed(() => {
      return configStore.getEnvConfigValue(ENV_CONFIG_PROPERTY.INSPECTION_COMMENT_MAX_LENGTH);
    });

    const updateDuration = () => {
      if (surfaceConditionUpdated.value) {
        deckValue.value.deckWsCondRateDurationChanges = inspectionStore.setDurationChanges(
          deckValue.value.deckWsCondRateDurationChanges,
        );
        deckValue.value.deckWsCondRateDuration = inspectionStore.setDuration(
          deckValue.value.deckWsCondRateDuration,
          deckValue.value.deckWsCondRateDurationChanges,
        );
      }

      if (deckConditionUpdated.value) {
        deckValue.value.dkRatingDurationChanges = inspectionStore.setDurationChanges(
          deckValue.value.dkRatingDurationChanges,
        );
        deckValue.value.dkRatingDuration = inspectionStore.setDuration(
          deckValue.value.dkRatingDuration,
          deckValue.value.dkRatingDurationChanges,
        );
      }

      deckConditionUpdated.value = false;

      surfaceConditionUpdated.value = false;

      conditionUpdated.value = false;
    };

    const updateWearingSurfacedThicknessDate = () => {
      if (wearingSurfaceThicknessUpdated.value) {
        deckValue.value.wearingSurfaceThicknessDate = new Date();
        wearingSurfaceThicknessUpdated.value = false;
      }
    };

    const updateApproachRecordedDate = () => {
      deckValue.value.apprWsThickDate = new Date();
    };

    const updateDate = () => {
      deckValue.value.EST_CHLORIDE_CONTENT_DATE = new Date();
    };

    const toggleAllPanels = (expandAll) => {
      isExpanded.value = expandAll;
      if (expandAll) {
        panel.value = allPanels;
      } else {
        panel.value = [];
      }
    };

    const isRequiredByReportGroup = computed({
      get() {
        const requiredReportGroups = new Set(["S1", "L1", "A1"]);
        if (props.moduleType == MODULE_TYPES.MANAGEMENT) {
          return requiredReportGroups.has(structureHeader.value?.REPORTGROUP?.trim());
        } else {
          return requiredReportGroups.has(structure.value?.structureBridge?.reportGroup?.trim());
        }
      },
    });

    const saveChanges = async () => {
      const isSuccess = await deckStore.saveDeck(props.moduleType, deckValue.value, structure.value);
      if (isSuccess) {
        if (props.moduleType == MODULE_TYPES.INSPECTION) {
          inspectionStore.setLowestConditionRating();
        } else {
          inspectionLinksStore.isDirty = false;
        }
        await readData();
      }
      return isSuccess;
    };

    const cancelChanges = async () => {
      resetDirty.value = true;
      deckStore.resetPageData();
      deckValue.value = deckStore.getDeckObject();
      // Using this flag to not set the dirty flag in watch
      inspectionLinksStore.isDirty = false;
    };

    const setUpdatedComment = (comment, commentType) => {
      const foundComment = deckValue.value.inspectionComments.find((c) => {
        return c.commentType === commentType;
      });
      if (foundComment) {
        foundComment.notes = comment.notes;
      } else {
        deckValue.value.inspectionComments.push(comment);
      }
    };

    const hasAccessToEdit = computed({
      get() {
        if (structureHeader.value?.INSPECTIONSTATUS === INSPECTION_STATUSES.ACCEPTED) {
          return userPermissionStore.checkUserPermission(
            INSPECTION_LINKS_DECK_PERMISSIONS[ACCESS_LEVELS.CUSTOM.EDIT_APPROVED_INSPECTION],
            userPermissionStore.getUserPermissionByBrkeyFromCache(deckValue.value.brkey),
          );
        } else {
          return userPermissionStore.checkUserPermission(
            INSPECTION_LINKS_DECK_PERMISSIONS[ACCESS_LEVELS.EDIT],
            userPermissionStore.getUserPermissionByBrkeyFromCache(deckValue.value.brkey),
          );
        }
      },
    });

    const printD491 = () => {
      return { reportPage: "D491-Insp-Deck-Screen", marker: SNACKBAR_MARKER.DECK };
    };

    const showErrorMessage = () => {
      if ((snackBarId.value === null || snackBarId.value === "") && !quickSearchStore.getErrorMessage()) {
        snackBarId.value = snackbarStore.showMessage({
          displayText: COMMON_MESSAGE_CONSTANTS.OPERATION_FAILED,
          messageType: SNACKBAR_MESSAGE_TYPES.ERROR_CUSTOM,
          timeout: 10000,
          callback: () => {
            snackBarId.value = null;
          },
        });
      }
    };

    // Gets the lowest minSpanOut2OutWidth from the Structure Units
    // that's not null and greater than 0
    const minimumOut2OutWidth = computed({
      get() {
        return getMinimumOut2OutWidthDisplay(deckValue.value?.structureUnits);
      },
    });

    // watch those objects used in component from structure to warn about unsaved changes
    watch(
      [deckValue],
      () => {
        // This watch will NOT be triggered during initial onMounted()
        if (props.moduleType === MODULE_TYPES.MANAGEMENT) {
          // for management module
          if (resetDirty.value) {
            inspectionLinksStore.isDirty = false;
            resetDirty.value = false;
          } else {
            inspectionLinksStore.isDirty = true;
          }
        } else if (props.moduleType === MODULE_TYPES.INSPECTION) {
          // for inspection  module
          if (resetDirty.value) {
            inspectionStore.setDirtyFlag(false);
            resetDirty.value = false;
          } else {
            inspectionStore.setDirtyFlag(true);
          }
        }
      },
      { deep: true },
    );

    watch(
      () => [props],
      () => {
        readData();
      },
      { deep: true },
    );

    watch(
      () => deckValue.value.wearingSurfaceThickness,
      (newValue, oldValue) => {
        wearingSurfaceThicknessUpdated.value = false;
        if (newValue !== undefined && oldValue !== undefined && newValue !== oldValue) {
          wearingSurfaceThicknessUpdated.value = true;
        }
      },
    );

    return {
      valueInRangeRule,
      deckSpanBySpanRef,
      isExpanded,
      structureHeader,
      valid,
      inspectionDeckForm,
      conditionUpdated,
      updateDuration,
      surfaceConditionUpdated,
      deckConditionUpdated,
      panel,
      isEditable,
      deckValue,
      conditionRatings,
      deckSurfaceTypes,
      deckMembraneTypes,
      deckProtectionTypes,
      getHistDuration,
      commentsMaxLength,
      INSPECTION_COMMENTS,
      getFormattedDateStringNoTime,
      configStore,
      REFERENCE_TABLE,
      validate,
      requiredRule,
      lessThanOneRule,
      greaterThanZeroRule,
      minYearLengthRule,
      ROUNDING_FACTOR,
      lessThanValueRule,
      DECK_MAX_VALUE,
      updateWearingSurfacedThicknessDate,
      updateApproachRecordedDate,
      updateDate,
      toggleAllPanels,
      isRequiredByReportGroup,
      readData,
      saveChanges,
      cancelChanges,
      setUpdatedComment,
      MODULE_TYPES,
      inspectionStore,
      printD491,
      minimumOut2OutWidth,
      wearingSurfaceThicknessUpdated,
    };
  },
  components: {
    DeckSpanBySpan,
    CommentTypeTextField,
    LabelSelect,
    LabelText,
    LabelDatefield,
    ExpandCollapseToggle,
    ManagementCommentTypeTextField,
    ValidationCountsHeader,
  },
};
</script>
